<template>
  <StackPageLayout @close="handleClose">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <PageTitle> Edit Products </PageTitle>

        <RouterLink
          #default="{ navigate }"
          custom
          :to="{ name: 'settingsProductCreate' }"
        >
          <ButtonAction @click="navigate">
            Add Product
          </ButtonAction>
        </RouterLink>
      </div>
    </template>

    <div
      class="h-full overflow-hidden rounded-lg bg-background py-3"
    >
      <ProductVirtualList>
        <template #category="item">
          <ProductListItemCategory
            :category="item.category"
          />
        </template>
        <template #product="item">
          <div class="flex">
            <RouterLink
              #default="{ navigate }"
              :to="{
                name: 'settingsProductEdit',
                params: { productId: item.product.id },
              }"
              custom
            >
              <SettingsProductItem
                :product="item.product"
                @click="navigate"
                @click-dots="
                  handleClickDots(item.product.id)
                "
              />
            </RouterLink>
            <button
              type="button"
              class="flex w-8 items-center justify-center"
              @click="handleClickDots(item.product.id)"
            >
              <IconVerticalEllipsis class="size-6" />
            </button>
          </div>
        </template>
      </ProductVirtualList>
    </div>
  </StackPageLayout>

  <BaseDrawer
    shrink
    :is-open="isEditDeleteDrawerOpen"
    @close="cancelEditDelete"
  >
    <div class="h-full overflow-y-auto px-3 pb-4">
      <ActionItemSmall
        label="Edit Product"
        :icon="IconEdit"
        @click="confirmEditDelete('edit')"
      />

      <ActionItemSmall
        label="Delete Product"
        :icon="IconTrash"
        @click="confirmEditDelete('delete')"
      />
    </div>
  </BaseDrawer>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    description="Are you sure you wish to delete this product?"
    confirm-label="Delete Product"
    cancel-label="No"
  />
</template>

<script setup lang="ts">
import {
  IconEdit,
  IconTrash,
  IconVerticalEllipsis,
} from "@/icons"

import { deleteProduct } from "@/services/products/deleteProduct"

const router = useRouter()
const handleClose = () => router.push({ name: "moreMenu" })

const {
  isRevealed: isEditDeleteDrawerOpen,
  reveal: showEditDeleteDraw,
  confirm: confirmEditDelete,
  cancel: cancelEditDelete,
} = useConfirmDialog()
const handleClickDots = async (productId: string) => {
  const { data, isCanceled } = await showEditDeleteDraw()
  if (isCanceled) return

  if (data === "edit") {
    router.push({
      name: "settingsProductEdit",
      params: {
        productId: productId,
      },
    })
  } else if (data === "delete") {
    if (deleteConfirmDialogRef.value === null) return
    const { isCanceled: isDeleteCancelled } =
      await deleteConfirmDialogRef.value.reveal()
    if (isDeleteCancelled) return

    try {
      await deleteProduct(productId)
      toast.success("Product deleted!")
    } catch {
      toast.error("Failed to delete product")
    }
  }
}

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)
</script>
